import { type DragEvent, type ReactNode, useRef, useState } from "react"
import { FOCO } from "./foco"

const ACEITOS = [".pdf", ".jpg", ".jpeg", ".png"]

const aceito = (arquivo: File) =>
    ACEITOS.some((extensao) => arquivo.name.toLowerCase().endsWith(extensao))

interface Props {
    nome: string
    rotulo: string
    ajuda: ReactNode
    maximo: number
    arquivos: File[]
    onMudar: (arquivos: File[]) => void
}

export const CampoArquivo = ({ nome, rotulo, ajuda, maximo, arquivos, onMudar }: Props) => {
    const seletor = useRef<HTMLInputElement>(null)
    const [arrastando, setArrastando] = useState(false)
    const [recusado, setRecusado] = useState(false)

    // Passou do limite, ficam os mais recentes: com um arquivo só, escolher outro é trocar.
    const receber = (lista: FileList | null) => {
        const novos = Array.from(lista ?? [])
        const validos = novos.filter(aceito)
        setRecusado(validos.length < novos.length)
        if (validos.length) onMudar([...arquivos, ...validos].slice(-maximo))
    }

    const soltar = (evento: DragEvent<HTMLButtonElement>) => {
        evento.preventDefault()
        setArrastando(false)
        receber(evento.dataTransfer.files)
    }

    const sair = (evento: DragEvent<HTMLButtonElement>) => {
        if (!evento.currentTarget.contains(evento.relatedTarget as Node | null)) {
            setArrastando(false)
        }
    }

    return (
        <div className="flex flex-col gap-1.5">
            <label id={`rotulo-${nome}`} htmlFor={`campo-${nome}`} className="font-semibold">
                {rotulo}
            </label>
            <p id={`ajuda-${nome}`} className="max-w-[66ch] text-sm text-soft [&_b]:text-ink">
                {ajuda}
            </p>
            <input
                ref={seletor}
                id={`campo-${nome}`}
                type="file"
                accept={ACEITOS.join(",")}
                multiple={maximo > 1}
                hidden
                onChange={(evento) => {
                    receber(evento.target.files)
                    evento.target.value = ""
                }}
            />
            <button
                type="button"
                aria-labelledby={`rotulo-${nome}`}
                aria-describedby={`instrucao-${nome} ajuda-${nome}`}
                onClick={() => seletor.current?.click()}
                onDragEnter={() => setArrastando(true)}
                onDragOver={(evento) => evento.preventDefault()}
                onDragLeave={sair}
                onDrop={soltar}
                className={`flex min-h-24 max-w-xl flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-5 text-center text-sm transition-colors ${
                    arrastando
                        ? "border-brand bg-brand-wash text-ink"
                        : "border-line-strong bg-card text-soft hover:border-brand"
                } ${FOCO}`}
            >
                <span id={`instrucao-${nome}`} className="font-semibold text-ink">
                    Arraste o arquivo aqui ou clique para escolher
                </span>
                <span className="text-xs">
                    PDF, JPG ou PNG · {maximo === 1 ? "1 arquivo" : `até ${maximo} arquivos`}
                </span>
            </button>
            {recusado && (
                <p className="text-sm text-off">Só aceitamos arquivos em PDF, JPG ou PNG.</p>
            )}
            {arquivos.length > 0 && (
                <ul className="flex max-w-xl flex-col gap-1.5">
                    {arquivos.map((arquivo, indice) => (
                        <li
                            key={`${arquivo.name}-${indice}`}
                            className="flex items-center justify-between gap-3 rounded-lg border border-line bg-card px-3 py-2 text-sm"
                        >
                            <span className="min-w-0 truncate">{arquivo.name}</span>
                            <button
                                type="button"
                                aria-label={`Remover ${arquivo.name}`}
                                onClick={() => onMudar(arquivos.filter((_, i) => i !== indice))}
                                className={`shrink-0 text-soft underline hover:text-ink ${FOCO}`}
                            >
                                Remover
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    )
}
